import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Divider,
  Skeleton,
  SkeletonText,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Heading,
  Tooltip,
} from "@chakra-ui/react";
import { EditIcon, InfoIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { IntegrationDetailForm } from "../../forms/IntegrationDetailForm";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { useLightspeedAuthApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { CopyText } from "../../components/Utilities/CopyText";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { ServiceManagementPermissions } from "../../enums/ServiceManagementPermissions";
import {
  getServiceManagementFeatureDescription,
  getServiceManagementFeatureDisplayName,
  getServiceManagementFeatureManagePath,
  getServiceManagementFeatureRequiredParameterKey,
  getServiceManagementFeatureRequiredParameterLabel,
  isServiceManagementFeatureHidden,
  isServiceManagementFeatureRunnable,
  ServiceManagementFeatures,
} from "../../enums/ServiceManagementFeatures";
import { ApiResult } from "../../api/dataApiClient";
import { IntegrationFeatureResult } from "../../components/Integration/IntegrationsFeatureResult";
import {
  IntegrationFeatureParameter,
  IntegrationGetMMSListParameter,
  IntegrationGetWorkerListParameter,
  IntegrationGetWorkerStatusParameter,
} from "../../components/Integration/IntegrationsFeatureParameter";
import { ListViewDictionary } from "../../components/ListView/ListViewDictionary";
import { ListView } from "../../components/ListView/ListView";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";

interface IntegrationDetailProps {
  create?: boolean;
}

export interface IntegrationFeature {
  name: ServiceManagementFeatures;
  permission: ServiceManagementPermissions;
  isLoading: boolean;
  result?: any;
  parameter?: any;
}

export const IntegrationDetail = (props: IntegrationDetailProps) => {
  const [searchParams] = useSearchParams();
  const { businessId, integrationId } = useParams();
  const navigate = useNavigate();
  const adminApiRequest = useAdminApiClient();
  const [integration, setIntegration] =
    useState<CherryHubAdminApi.Integration | null>();
  const [features, setFeatures] = useState<IntegrationFeature[]>();
  const { data: integrationDoc, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getBusinessIntegration(businessId, integrationId);
    },
    [businessId, integrationId]
  );
  const { data: allIntegrations } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId) return null;
      return apiClient.getBusinessIntegrations(businessId, {});
    },
    [businessId]
  );

  const {
    data: serviceMetadata,
    error: serviceMetadataError,
    statusCode: serviceMetadataStatusCode,
    result: serviceMetadataResult,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getServiceManagementMetadata(businessId, integrationId);
    },
    [businessId, integrationId]
  );

  const { data: accessRights } =
    useAdminApiRequest<CherryHubAdminApi.ServiceManagementPermissions>(
      (apiClient) => {
        if (!businessId || !integrationId) {
          return null;
        }
        return apiClient.getIntegrationPermissions(businessId, integrationId);
      },
      [businessId, integrationId]
    );

  const integrationNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    allIntegrations?.Results?.forEach((item) => {
      if (item.Meta?.Id && item.DisplayName) {
        map.set(item.Meta.Id, item.DisplayName);
      }
    });
    return map;
  }, [allIntegrations]);

  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  const authorizeUrlRequest = useLightspeedAuthApiUpdateRequest(
    (client, integrationId: string) => client.getAuthorizeUrl(integrationId),
    []
  );
  const authorizeUrlDisclosure = useDisclosure();

  useEffect(() => {
    setIntegration(integrationDoc);
  }, [integrationDoc]);

  useEffect(() => {
    if (!authorizeUrlRequest.isLoading && authorizeUrlRequest.result) {
      if (authorizeUrlRequest.result.ok) {
        authorizeUrlDisclosure.onOpen();
      } else {
        showErrorToast(
          authorizeUrlRequest.result.message ?? "Failed to get auth URL."
        );
      }
    }
  }, [authorizeUrlRequest.isLoading, authorizeUrlRequest.result]);

  const onSuccess = useCallback(
    (integration: CherryHubAdminApi.Integration) => {
      setIntegration(integration);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Integration created.");
        navigate(
          `/admin/businesses/${businessId}/integrations/${integration.Meta?.Id}`
        );
      } else {
        showSuccessToast("Integration updated.");
      }
    },
    [showSuccessToast, integration]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Integration update failed.");
    },
    [showErrorToast]
  );

  const finishFeatureRun = useCallback(
    (featureName: string, result?: unknown, errorMessage?: string) => {
      if (errorMessage) {
        showErrorToast(errorMessage);
      } else if (result !== undefined) {
        showSuccessToast(`${featureName} run successfully.`);
      }
      setFeatures((prev) =>
        (prev ?? []).map((item) => {
          if (item.name !== featureName) {
            return item;
          }
          return {
            ...item,
            isLoading: false,
            ...(errorMessage || result === undefined ? {} : { result }),
          };
        })
      );
    },
    [showErrorToast, showSuccessToast]
  );

  const runFeature = useCallback(
    (featureName: string, param?: any) => {
      const requiredParameterKey =
        getServiceManagementFeatureRequiredParameterKey(featureName);
      if (requiredParameterKey && !param?.[requiredParameterKey]) {
        const label =
          getServiceManagementFeatureRequiredParameterLabel(featureName) ??
          "the required parameter";
        showErrorToast(`Choose ${label} first.`);
        return;
      }

      if (!isServiceManagementFeatureRunnable(featureName)) {
        showErrorToast("This feature cannot be run from here.");
        return;
      }

      setFeatures((prev) =>
        (prev ?? []).map((item) =>
          item.name === featureName ? { ...item, isLoading: true } : item
        )
      );
      let result: Promise<ApiResult<any>> | null;
      switch (featureName) {
        case ServiceManagementFeatures.GetServiceApiEnvironmentDetails:
          result = adminApiRequest.getIntegrationEnvironment(
            businessId!,
            integrationId!
          );
          break;
        case ServiceManagementFeatures.GetServiceOutboundIpDetails:
          result = adminApiRequest.getIntegrationOutboundIpDetails(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetServiceConfigurationValues:
          result = adminApiRequest.getIntegrationEnvironmentConfiguration(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationMMSListTypes:
          result = adminApiRequest.getIntegrationMMSListTypes(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationMMSListAll:
          result = adminApiRequest.getIntegrationMMSListAll(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationMMSList: {
          let parameter = param as IntegrationGetMMSListParameter;
          result = adminApiRequest.getIntegrationMMSList(
            businessId!,
            integrationId!,
            parameter.type
          );
          break;
        }

        case ServiceManagementFeatures.PerformIntegrationMMSConnectionTest:
          result = adminApiRequest.getIntegrationMMSConnectionTest(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.PerformIntegrationMMSConnectionClose:
          result = adminApiRequest.getIntegrationMMSConnectionClose(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationMMSConnectionStatus:
          result = adminApiRequest.getIntegrationMMSConnectionStatus(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.PerformIntegrationMLSConnectionTest:
          result = adminApiRequest.getIntegrationMLSConnectionTest(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.PerformIntegrationMLSConnectionClose:
          result = adminApiRequest.getIntegrationMLSConnectionClose(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationMLSConnectionStatus:
          result = adminApiRequest.getIntegrationMLSConnectionStatus(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationWorkerList: {
          let parameter = param as IntegrationGetWorkerListParameter;
          result = adminApiRequest.getIntegrationWorkerList(
            businessId!,
            integrationId!,
            parameter.listTypeId
          );
          break;
        }

        case ServiceManagementFeatures.GetIntegrationWorkerListAll:
          result = adminApiRequest.getIntegrationWorkerListAll(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationWorkerListTypes:
          result = adminApiRequest.getIntegrationWorkerListTypes(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.PerformIntegrationWorkerStart:
          result = adminApiRequest.getIntegrationWorkerStart(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.PerformIntegrationWorkerStop:
          result = adminApiRequest.getIntegrationWorkerAbort(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationWorkerStatus: {
          let parameter = param as IntegrationGetWorkerStatusParameter;
          result = adminApiRequest.getIntegrationWorkerStatus(
            businessId!,
            integrationId!,
            parameter.executionId
          );
          break;
        }

        case ServiceManagementFeatures.GetIntegrationWorkerStatusAll:
          result = adminApiRequest.getIntegrationWorkerStatusAll(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetIntegrationSecretConfiguration:
          result = adminApiRequest.getIntegrationSecret(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.GetServiceManagementMetadata:
          result = adminApiRequest.getServiceManagementMetadata(
            businessId!,
            integrationId!
          );
          break;

        case ServiceManagementFeatures.BootstrapIntegrationConfiguration:
          result = adminApiRequest.bootstrapIntegrationConfiguration(
            businessId!,
            integrationId!
          );
          break;

        default:
          result = null;
          break;
      }
      if (!result) {
        finishFeatureRun(
          featureName,
          undefined,
          "This feature cannot be run from here."
        );
        return;
      }
      result.then((response) => {
        if (response.ok) {
          finishFeatureRun(featureName, response.data);
        } else {
          finishFeatureRun(featureName, undefined, response.message);
        }
      });
    },
    [
      adminApiRequest,
      businessId,
      integrationId,
      finishFeatureRun,
      showErrorToast,
    ]
  );

  useEffect(() => {
    if (!serviceMetadata) {
      setFeatures(undefined);
      return;
    }
    const supportedFeatures =
      serviceMetadata.SupportedCloudServiceManagementFeatures;
    if (!supportedFeatures) {
      setFeatures([]);
      return;
    }
    const nextFeatures = Object.entries(supportedFeatures)
      .filter(([key]) => !isServiceManagementFeatureHidden(key))
      .map(([key, value]) => {
        const feature: IntegrationFeature = {
          name: key as ServiceManagementFeatures,
          permission: value as ServiceManagementPermissions,
          isLoading: false,
        };
        return feature;
      });
    setFeatures(nextFeatures);
  }, [serviceMetadata]);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Integration
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/integrations`}
              >
                Integrations
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/integrations/${
                  props.create ? "create" : integration?.id
                }`}
              >
                {props.create ? "New" : integration?.IntegrationName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || !integration) && !props.create && (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <HStack w="100%" justifyContent="space-between">
                <Skeleton w="320px" height="40px" />
                <Skeleton w="80px" height="40px" />
              </HStack>
              <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
              <SkeletonText w="100%" mt="4" noOfLines={4} spacing="4" />
            </VStack>
          )}

          {!isLoading && editMode && (
            <IntegrationDetailForm
              apiClient={apiClient}
              businessId={businessId!}
              integration={integration!}
              onCancel={() =>
                props.create
                  ? navigate(`/admin/businesses/${businessId}/integrations`)
                  : setEditMode(false)
              }
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}

          {!isLoading && !editMode && integration && (
            <>
              <HStack w="100%">
                <Box flexGrow={1} pb={4}>
                  <CopyText text={integration?.Meta?.Id!} hover adjustMargin>
                    <Text fontSize="md" color="cherryUi.600">
                      {integration?.id} / {integration?.IntegrationNo}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{integration?.IntegrationName}</b>
                  </Text>
                </Box>

                <HStack spacing={2}>
                  {integration?.SystemImplementations?.includes(
                    "LightspeedOSeriesAPI"
                  ) && (
                    <Button
                      colorScheme="cherryButton"
                      isLoading={authorizeUrlRequest.isLoading}
                      onClick={() =>
                        authorizeUrlRequest.request(integrationId!)
                      }
                    >
                      Get Auth URL
                    </Button>
                  )}
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </HStack>
              </HStack>

              <ViewControl
                label="Enabled"
                value={integration?.Enabled ? "Yes" : "No"}
              />

              <ViewControl
                label="Loyalty Configuration From Venue Id"
                value={integration?.LoyaltyConfigurationFromVenueId}
              />

              <ViewControl
                label="Record Override Integration No"
                value={integration?.RecordOverrideIntegrationNo}
              />

              <ViewControl
                label="Source System Name"
                value={integration?.SourceSystemName}
              />

              <ViewControl
                label="Source System Type"
                value={integration?.SourceSystemType}
              />

              <ViewControl label="System Implementations">
                <UnorderedList flex={3}>
                  {integration?.SystemImplementations?.map((system) => {
                    return (
                      <ListItem ml={4} key={system}>
                        {system}
                      </ListItem>
                    );
                  })}
                </UnorderedList>
              </ViewControl>

              <ViewControl label="Enabled Services">
                <UnorderedList flex={3}>
                  {integration?.EnabledServices?.map((service) => {
                    return (
                      <ListItem ml={4} key={service}>
                        {service}
                      </ListItem>
                    );
                  })}
                </UnorderedList>
              </ViewControl>

              <ViewControl label="Linked Integrations" pb={6}>
                {integration?.LinkedIntegrations?.length ? (
                  <UnorderedList flex={3}>
                    {integration.LinkedIntegrations.map((id) => {
                      const name = integrationNameMap.get(id);
                      return (
                        <ListItem ml={4} key={id}>
                          {name ? `${name} (${id})` : id}
                        </ListItem>
                      );
                    })}
                  </UnorderedList>
                ) : (
                  <Text flex={3}>None</Text>
                )}
              </ViewControl>

              <Tabs variant="edit" w="100%" isLazy>
                <TabList>
                  <HStack spacing={3}>
                    <Tab>General</Tab>
                    <Tab>Service Management</Tab>
                  </HStack>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <Accordion
                      w="100%"
                      allowToggle
                      allowMultiple
                      variant="section"
                    >
                      <AccordionItem>
                        {({ isExpanded }) => (
                          <>
                            <h2>
                              <AccordionButton>
                                <Box flex="1" textAlign="left">
                                  <b>Configuration</b>
                                </Box>
                                {isExpanded ? (
                                  <ChevronUpIcon color="white" fontSize="xl" />
                                ) : (
                                  <AccordionIcon />
                                )}
                              </AccordionButton>
                            </h2>
                            <AccordionPanel width="100%">
                              <Box
                                width="100%"
                                borderWidth="1px"
                                borderRadius="lg"
                                boxShadow="lg"
                                overflowX="auto"
                                padding={6}
                                position="relative"
                                minHeight="90px"
                              >
                                {/*
                                  Edit intentionally links to the generic JSON configuration
                                  editor (no :configurationTypeName). Typed editors
                                  (EMDIS / eBet / IGTA) need a follow-up PR: routing unknown
                                  types to the IGTA form was wrong, and the EMMMS
                                  configuration form is too large to land here safely.
                                */}
                                <Button
                                  as={Link}
                                  to={`/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}/configuration`}
                                  leftIcon={<EditIcon />}
                                  colorScheme="cherryButton"
                                  position="absolute"
                                  right={6}
                                >
                                  Edit
                                </Button>
                                <pre>
                                  {integration?.Configuration == null
                                    ? ""
                                    : JSON.stringify(
                                        integration?.Configuration,
                                        null,
                                        2
                                      )}
                                </pre>
                              </Box>
                            </AccordionPanel>
                          </>
                        )}
                      </AccordionItem>

                      <AccordionItem>
                        {({ isExpanded }) => (
                          <>
                            <h2>
                              <AccordionButton>
                                <Box flex="1" textAlign="left">
                                  <b>Data Store</b>
                                </Box>
                                {isExpanded ? (
                                  <ChevronUpIcon color="white" fontSize="xl" />
                                ) : (
                                  <AccordionIcon />
                                )}
                              </AccordionButton>
                            </h2>
                            <AccordionPanel width="100%">
                              <Box
                                width="100%"
                                borderWidth="1px"
                                borderRadius="lg"
                                boxShadow="lg"
                                overflowX="auto"
                                padding={6}
                                position="relative"
                                minHeight="90px"
                              >
                                <Button
                                  as={Link}
                                  to={`/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}/data-store`}
                                  leftIcon={<EditIcon />}
                                  colorScheme="cherryButton"
                                  position="absolute"
                                  right={6}
                                >
                                  Edit
                                </Button>
                                <pre>
                                  {integration?.DataStore == null
                                    ? ""
                                    : JSON.stringify(
                                        integration?.DataStore,
                                        null,
                                        2
                                      )}
                                </pre>
                              </Box>
                            </AccordionPanel>
                          </>
                        )}
                      </AccordionItem>

                      {integration?.EnabledServices &&
                        integration?.EnabledServices?.filter(
                          (i) => i === "MembershipManagementService"
                        ).length > 0 && (
                          <>
                            <AccordionItem>
                              {({ isExpanded }) => (
                                <>
                                  <h2>
                                    <AccordionButton>
                                      <Box flex="1" textAlign="left">
                                        <b>
                                          Membership Management Service
                                          Configuration
                                        </b>
                                      </Box>
                                      {isExpanded ? (
                                        <ChevronUpIcon
                                          color="white"
                                          fontSize="xl"
                                        />
                                      ) : (
                                        <AccordionIcon />
                                      )}
                                    </AccordionButton>
                                  </h2>
                                  <AccordionPanel width="100%">
                                    <Box w="100" position="relative">
                                      <Button
                                        as={Link}
                                        to={`/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}/membership-management-configuration`}
                                        leftIcon={<EditIcon />}
                                        colorScheme="cherryButton"
                                        position="absolute"
                                        right={6}
                                      >
                                        Edit
                                      </Button>
                                      <VStack w="100%" alignItems="flex-start">
                                        <ViewControl
                                          label="Member Key"
                                          value={
                                            integration
                                              ?.MembershipManagementServiceConfiguration
                                              ?.MemberKey ?? "-"
                                          }
                                        />

                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl
                                          label="Member Photo Key"
                                          value={
                                            integration
                                              ?.MembershipManagementServiceConfiguration
                                              ?.MemberPhotoKey ?? "-"
                                          }
                                        />

                                        <Divider
                                          variant="section"
                                          marginInlineStart="-1rem !important"
                                          w="calc(100% + 2rem)"
                                        />

                                        <ViewControl
                                          label="Membership Renewal Fee Key"
                                          value={
                                            integration
                                              ?.MembershipManagementServiceConfiguration
                                              ?.MembershipRenewalFeeKey ?? "-"
                                          }
                                        />
                                      </VStack>
                                    </Box>
                                  </AccordionPanel>
                                </>
                              )}
                            </AccordionItem>
                          </>
                        )}
                      {integration?.EnabledServices &&
                        integration?.EnabledServices?.filter(
                          (i) => i === "MembershipLoyaltyService"
                        ).length > 0 && (
                          <>
                            <AccordionItem>
                              {({ isExpanded }) => (
                                <>
                                  <h2>
                                    <AccordionButton>
                                      <Box flex="1" textAlign="left">
                                        <b>
                                          Membership Loyalty Service
                                          Configuration
                                        </b>
                                      </Box>
                                      {isExpanded ? (
                                        <ChevronUpIcon
                                          color="white"
                                          fontSize="xl"
                                        />
                                      ) : (
                                        <AccordionIcon />
                                      )}
                                    </AccordionButton>
                                  </h2>
                                  <AccordionPanel width="100%">
                                    <Box w="100" position="relative">
                                      <Button
                                        as={Link}
                                        to={`/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}/membership-loyalty-configuration`}
                                        leftIcon={<EditIcon />}
                                        colorScheme="cherryButton"
                                        position="absolute"
                                        right={6}
                                      >
                                        Edit
                                      </Button>

                                      <VStack w="100%" alignItems="flex-start">
                                        <ViewControl
                                          label="Member Key"
                                          value={
                                            integration
                                              ?.MembershipLoyaltyServiceConfiguration
                                              ?.MemberKey ?? "-"
                                          }
                                        />

                                        <ViewControl
                                          label="Points Award Enabled"
                                          value={
                                            integration?.MembershipLoyaltyServiceConfiguration ===
                                            null
                                              ? "Yes"
                                              : integration
                                                  ?.MembershipLoyaltyServiceConfiguration
                                                  ?.PointsAwardEnabled
                                              ? "Yes"
                                              : "No"
                                          }
                                        />

                                        <ViewControl
                                          label="Points Redeem Enabled"
                                          value={
                                            integration?.MembershipLoyaltyServiceConfiguration ===
                                            null
                                              ? "Yes"
                                              : integration
                                                  ?.MembershipLoyaltyServiceConfiguration
                                                  ?.PointsRedeemEnabled
                                              ? "Yes"
                                              : "No"
                                          }
                                        />
                                      </VStack>
                                    </Box>
                                  </AccordionPanel>
                                </>
                              )}
                            </AccordionItem>
                          </>
                        )}
                    </Accordion>
                  </TabPanel>

                  <TabPanel>
                    <Accordion
                      w="100%"
                      allowToggle
                      allowMultiple
                      variant="section"
                    >
                      <AccordionItem>
                        {({ isExpanded }) => (
                          <>
                            <h2>
                              <AccordionButton>
                                <Box flex="1" textAlign="left">
                                  <b>Features</b>
                                </Box>
                                {isExpanded ? (
                                  <ChevronUpIcon color="white" fontSize="xl" />
                                ) : (
                                  <AccordionIcon />
                                )}
                              </AccordionButton>
                            </h2>
                            <AccordionPanel width="100%">
                              {isExpanded && (
                                <VStack w="100%" alignItems="flex-start">
                                  {serviceMetadataError ? (
                                    <ErrorMessage>
                                      {`HTTP ${
                                        serviceMetadataStatusCode ?? "unknown"
                                      }: ${
                                        serviceMetadataResult?.ok === false
                                          ? serviceMetadataResult.message
                                          : serviceMetadataError
                                      }`}
                                    </ErrorMessage>
                                  ) : (
                                    (features?.length &&
                                      features.map((feature, idx) => {
                                        const featureDescription =
                                          getServiceManagementFeatureDescription(
                                            feature.name
                                          );
                                        const featureDisplayName =
                                          getServiceManagementFeatureDisplayName(
                                            feature.name
                                          );

                                        return (
                                          <VStack
                                            key={feature.name}
                                            w="100%"
                                            alignItems="flex-start"
                                          >
                                            <HStack
                                              w="100%"
                                              alignItems="flex-start"
                                            >
                                              <HStack
                                                flexGrow={1}
                                                alignItems="center"
                                                spacing={1}
                                                pr={4}
                                              >
                                                <Heading size="xs">
                                                  {featureDisplayName}
                                                </Heading>
                                                {featureDescription && (
                                                  <Tooltip
                                                    label={`${feature.name}: ${featureDescription}`}
                                                    placement="right"
                                                    hasArrow
                                                    fontSize="xs"
                                                  >
                                                    <Box
                                                      as="button"
                                                      type="button"
                                                      display="inline-flex"
                                                      lineHeight={1}
                                                      aria-label={`About ${featureDisplayName}`}
                                                    >
                                                      <InfoIcon
                                                        boxSize={3.5}
                                                        color="gray.500"
                                                      />
                                                    </Box>
                                                  </Tooltip>
                                                )}
                                              </HStack>
                                              {(() => {
                                                const managePath =
                                                  getServiceManagementFeatureManagePath(
                                                    feature.name,
                                                    businessId!,
                                                    integration?.Meta?.Id ??
                                                      integrationId!,
                                                    serviceMetadata?.IntegrationConfigurationTypeName ??
                                                      undefined
                                                  );
                                                if (managePath) {
                                                  return (
                                                    <Button
                                                      as={Link}
                                                      to={managePath}
                                                      flexShrink={1}
                                                      size="xs"
                                                      colorScheme="cherryButton"
                                                      alignSelf="flex-start"
                                                    >
                                                      Open
                                                    </Button>
                                                  );
                                                }
                                                if (
                                                  !isServiceManagementFeatureRunnable(
                                                    feature.name
                                                  )
                                                ) {
                                                  return (
                                                    <Text
                                                      fontSize="xs"
                                                      color="gray.500"
                                                      alignSelf="flex-start"
                                                    >
                                                      Not available
                                                    </Text>
                                                  );
                                                }
                                                const requiredKey =
                                                  getServiceManagementFeatureRequiredParameterKey(
                                                    feature.name
                                                  );
                                                const missingParam = Boolean(
                                                  requiredKey &&
                                                    !feature.parameter?.[
                                                      requiredKey
                                                    ]
                                                );
                                                const requiredLabel =
                                                  getServiceManagementFeatureRequiredParameterLabel(
                                                    feature.name
                                                  );
                                                return (
                                                  <Button
                                                    flexShrink={1}
                                                    size="xs"
                                                    colorScheme="cherryButton"
                                                    alignSelf="flex-start"
                                                    isLoading={
                                                      feature.isLoading
                                                    }
                                                    isDisabled={missingParam}
                                                    title={
                                                      missingParam &&
                                                      requiredLabel
                                                        ? `Choose ${requiredLabel} first`
                                                        : undefined
                                                    }
                                                    onClick={() =>
                                                      runFeature(
                                                        feature.name,
                                                        feature.parameter
                                                      )
                                                    }
                                                  >
                                                    Run
                                                  </Button>
                                                );
                                              })()}
                                            </HStack>
                                            <IntegrationFeatureParameter
                                              feature={feature}
                                              onParameterChange={(
                                                fieldName,
                                                value
                                              ) => {
                                                setFeatures((prev) =>
                                                  (prev ?? []).map((item) =>
                                                    item.name === feature.name
                                                      ? {
                                                          ...item,
                                                          parameter: {
                                                            ...item.parameter,
                                                            [fieldName]: value,
                                                          },
                                                        }
                                                      : item
                                                  )
                                                );
                                              }}
                                            />
                                            {feature.result != null && (
                                              <Accordion
                                                allowMultiple
                                                variant="detail-section"
                                                w="100%"
                                                defaultIndex={[0]}
                                              >
                                                <AccordionItem>
                                                  <AccordionButton>
                                                    <Text fontWeight="bold">
                                                      {featureDisplayName}{" "}
                                                      Result
                                                    </Text>
                                                    <AccordionIcon />
                                                  </AccordionButton>
                                                  <AccordionPanel width="100%">
                                                    <IntegrationFeatureResult
                                                      name={feature.name}
                                                      result={feature.result}
                                                    />
                                                  </AccordionPanel>
                                                </AccordionItem>
                                              </Accordion>
                                            )}
                                            {idx != features.length - 1 && (
                                              <Divider />
                                            )}
                                          </VStack>
                                        );
                                      })) || (
                                      <Text fontStyle="italic">
                                        Features list is empty
                                      </Text>
                                    )
                                  )}
                                </VStack>
                              )}
                            </AccordionPanel>
                          </>
                        )}
                      </AccordionItem>

                      <AccordionItem>
                        {({ isExpanded }) => (
                          <>
                            <h2>
                              <AccordionButton>
                                <Box flex="1" textAlign="left">
                                  <b>Permissions</b>
                                </Box>
                                {isExpanded ? (
                                  <ChevronUpIcon color="white" fontSize="xl" />
                                ) : (
                                  <AccordionIcon />
                                )}
                              </AccordionButton>
                            </h2>
                            <AccordionPanel width="100%" position="relative">
                              <Button
                                as={Link}
                                to={`/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}/permissions`}
                                leftIcon={<EditIcon />}
                                colorScheme="cherryButton"
                                position="absolute"
                                right={6}
                              >
                                Edit
                              </Button>

                              <Accordion allowMultiple variant="detail-section">
                                <AccordionItem>
                                  <AccordionButton>
                                    <Text fontWeight="bold">
                                      Claim Based Authorisation
                                    </Text>
                                    <AccordionIcon />
                                  </AccordionButton>
                                  <AccordionPanel width="100%">
                                    <ListViewDictionary
                                      dictionary={
                                        accessRights?.ClaimBasedAuthorisation
                                      }
                                      emptyMessage="Claim Based Authorisation has not been set"
                                    />
                                  </AccordionPanel>
                                </AccordionItem>

                                <AccordionItem>
                                  <AccordionButton>
                                    <Text fontWeight="bold">
                                      User Based Authorisation
                                    </Text>
                                    <AccordionIcon />
                                  </AccordionButton>
                                  <AccordionPanel width="100%">
                                    <ListViewDictionary
                                      dictionary={
                                        accessRights?.UserBasedAuthorisation
                                      }
                                      emptyMessage="User Based Authorisation has not been set"
                                    />
                                  </AccordionPanel>
                                </AccordionItem>
                              </Accordion>
                            </AccordionPanel>
                          </>
                        )}
                      </AccordionItem>

                      <AccordionItem>
                        {({ isExpanded }) => (
                          <>
                            <h2>
                              <AccordionButton>
                                <Box flex="1" textAlign="left">
                                  <b>Integration Secret</b>
                                </Box>
                                {isExpanded ? (
                                  <ChevronUpIcon color="white" fontSize="xl" />
                                ) : (
                                  <AccordionIcon />
                                )}
                              </AccordionButton>
                            </h2>
                            {isExpanded && (
                              <AccordionPanel width="100%" position="relative">
                                <Button
                                  as={Link}
                                  to={`/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}/integration-secrets`}
                                  leftIcon={<EditIcon />}
                                  colorScheme="cherryButton"
                                  position="absolute"
                                  right={6}
                                >
                                  Edit
                                </Button>

                                <IntegrationSecretDetail
                                  businessId={businessId}
                                  integrationId={integrationId}
                                />
                              </AccordionPanel>
                            )}
                          </>
                        )}
                      </AccordionItem>
                    </Accordion>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </>
          )}
        </VStack>
      </PageContent>

      <Modal
        isOpen={authorizeUrlDisclosure.isOpen}
        onClose={authorizeUrlDisclosure.onClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Lightspeed Auth URL</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3}>
              Share this URL with the customer to complete the Lightspeed OAuth
              authorisation flow.
            </Text>
            <Text wordBreak="break-all">
              <b>{authorizeUrlRequest.data?.AuthorizationUrl}</b>
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="cherryButton"
              mr={3}
              onClick={() => {
                navigator.clipboard.writeText(
                  authorizeUrlRequest.data?.AuthorizationUrl ?? ""
                );
                showSuccessToast("Copied to clipboard!");
              }}
            >
              Copy to Clipboard
            </Button>
            <Button variant="ghost" onClick={authorizeUrlDisclosure.onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

interface IntegrationSecretDetailProps {
  businessId: string | undefined;
  integrationId: string | undefined;
}

const IntegrationSecretDetail = ({
  businessId,
  integrationId,
}: IntegrationSecretDetailProps) => {
  const { data: secrets, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getIntegrationSecret(businessId, integrationId);
    },
    [businessId, integrationId]
  );

  if (isLoading) {
    return (
      <HStack w="100%" p={6} boxShadow="lg" alignItems="flex-start" spacing={4}>
        <VStack w="100%" alignItems="flex-start" flex={1}>
          <Skeleton w="100px" height="20px" />
          <SkeletonText w="100%" mt="4" noOfLines={6} spacing="4" />
        </VStack>

        <VStack w="100%" alignItems="flex-start" flex={3}>
          <Skeleton w="100px" height="20px" />
          <SkeletonText w="100%" mt="4" noOfLines={6} spacing="4" />
        </VStack>
      </HStack>
    );
  }
  return <ListView list={secrets} />;
};
